import axios from 'axios';
import store from '@/store';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import { Endpoints, Mutations, Actions } from '@/ts/store/Constants';
import ErrorResponse from '@/ts/ErrorResponse';
import router from '@/router';
import UnitGroupings from '@/ts/UnitGroupings';
import UnitGroupingItem from '@/ts/UnitGroupingItem';
import GroupingUnitItem from '@/ts/GroupingUnitItem';
import GroupingResidentItem from '@/ts/GroupingResidentItem';
import { NotifyHelpers } from '@/ts/helpers/NotifyHelpers';

@Module({name: 'Groupings', namespaced: true})
export default class Groupings extends VuexModule {
    public allGroupings: UnitGroupingItem[] = [];
    public currentGrouping: any = null;
    public groupingTypes: any[] = [];
    public unitGroupings: UnitGroupings[] = [];
    public activeGroupingUnits: GroupingUnitItem[] = [];
    public activeGroupingResidents: GroupingResidentItem[] = [];

    @Mutation
    public RESET_GROUPINGS_STORE_STATE() {
        this.allGroupings            = [];
        this.currentGrouping         = [];
        this.groupingTypes           = [];
        this.unitGroupings           = [];
        this.activeGroupingUnits     = [];
        this.activeGroupingResidents = [];
    }

    get getAllGroupings(): UnitGroupingItem[] {
        return this.allGroupings;
    }

    get getCurrentGrouping() {
        return this.currentGrouping;
    }

    get getGroupingTypes() {
        return this.groupingTypes;
    }

    get getUnitGroupings(): UnitGroupings[] {
        return this.unitGroupings;
    }

    get getActiveGroupingUnits(): GroupingUnitItem[] {
        return this.activeGroupingUnits;
    }

    get getActiveGroupingResidents(): GroupingResidentItem[] {
        return this.activeGroupingResidents;
    }

    @Action
    public FETCH_ALL_GROUPINGS() {
        store.commit(Mutations.RESET_ALL_UNIT_GROUPINGS);

        axios.get(Endpoints.Admin.PropertyGroupings.GROUPINGS)
            .then((response: any) => {
                store.commit(Mutations.SET_ALL_GROUPINGS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_CURRENT_GROUPING(data: any) {
        return axios.get(Endpoints.Admin.PropertyGroupings.SINGLE_GROUPING(data.groupingId))
            .then((response: any) => {
                store.commit(Mutations.SET_CURRENT_GROUPING, response.data);

                if (data.action === 'preview') {
                    store.commit(Mutations.SET_CURRENT_GROUPING_UNITS, response.data);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_GROUPING_TYPES() {
        axios.get(Endpoints.Admin.PropertyGroupings.GROUPING_TYPES)
            .then((response: any) => {
                store.commit(Mutations.SET_GROUPING_TYPES, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_UNIT_GROUPINGS(unitId: number) {
        store.commit(Mutations.RESET_UNIT_GROUPINGS);

        axios.get(Endpoints.Admin.PropertyGroupings.UNIT_GROUPINGS(unitId))
            .then((response: any) => {
                if (response.data.length) {
                    store.commit(Mutations.SET_TENANTS_DEMOGRAPHIC_FLAYOUT_VISIBILITY, true);
                    store.commit(Mutations.SET_UNIT_GROUPINGS, response.data);
                } else {
                    const errorResponse = new ErrorResponse(429, 'Unit is not attached to any group');
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public ADD_NEW_GROUPING(data: any) {
        axios.post(Endpoints.Admin.PropertyGroupings.GROUPINGS, data)
            .then(() => {
                NotifyHelpers.notify('grouping_has_been_created', 'positive');
                router.push({name: 'admin-groupings'});
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public EDIT_GROUPING(data: any) {
        axios.put(Endpoints.Admin.PropertyGroupings.SINGLE_GROUPING(data.groupingId), data)
            .then(() => {
                NotifyHelpers.notify('grouping_has_been_updated', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public DELETE_GROUPING(data: any) {
        axios.delete(Endpoints.Admin.PropertyGroupings.SINGLE_GROUPING(data.groupingId))
            .then(() => {
                if (data.reload) {
                    router.push({name: 'admin-groupings'});
                } else {
                    store.commit(Mutations.REMOVE_GROUPING, data.groupingId);
                }

                store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
                NotifyHelpers.notify('grouping_has_been_deleted', 'positive');
            })
            .catch((error) => {
                if (error.response) {
                    store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });

        return;
    }

    @Action
    public FETCH_GROUPING_RESIDENTS(groupingId: number) {
        axios.post(Endpoints.Admin.PropertyGroupings.GROUPING_RESIDENTS(groupingId))
            .then((response) => {
                store.commit(Mutations.SET_CURRENT_GROUPING_RESIDENTS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });

        return;
    }

    @Action
    public REMOVE_UNIT_FROM_GROUPING(data: any) {
        axios.delete(Endpoints.Admin.PropertyGroupings.REMOVE_UNIT_FROM_GROUPING(data.groupingId, data.unitId))
            .then((response) => {
                store.commit(Mutations.REMOVE_UNIT_FROM_GROUPING_LIST, data.unitId);
            })
            .catch((error) => {
                if (error.response) {
                    store.commit(Mutations.RESET_CONFIRM_POPUP_DATA);
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });

        return;
    }

    @Mutation
    public SET_GROUPING_TYPES(data: any) {
        this.groupingTypes = data;
    }

    @Mutation
    public SET_CURRENT_GROUPING(data: any) {
        this.currentGrouping = data;
    }

    @Mutation
    public SET_CURRENT_GROUPING_UNITS(data: any) {
        this.activeGroupingUnits = [];

        for (const unit of data.units) {
            this.activeGroupingUnits.push(new GroupingUnitItem(
                unit.id,
                unit.project,
                unit.vacant === 'no' ? 'No' : 'Yes',
                unit.address,
                unit.residents.length,
                unit.titleNumber,
            ));
        }
    }

    @Mutation
    public SET_CURRENT_GROUPING_RESIDENTS(residents: any) {
        this.activeGroupingResidents = [];

        for (const resident of residents) {
            this.activeGroupingResidents.push(new GroupingResidentItem(
                resident.id,
                resident.firstName,
                resident.lastName,
                resident.address,
                resident.gender,
                resident.age,
                resident.interests,
                resident.educationTags,
            ));
        }
    }

    @Mutation
    public SET_ALL_GROUPINGS(data: any) {
        for (const grouping of data) {
            this.allGroupings.push(new UnitGroupingItem(
                grouping.id,
                grouping.name,
                grouping.typeName,
                grouping.updated_at,
                grouping.units_count,
            ));
        }
    }

    @Mutation
    public REMOVE_GROUPING(groupingId: number) {
        this.allGroupings = this.allGroupings.filter((grouping) => grouping.id !== groupingId);
    }

    @Mutation
    public SET_UNIT_GROUPINGS(groupings: any) {
        for (const grouping of groupings) {
            this.unitGroupings.push(new UnitGroupings(
                grouping.id,
                grouping.name,
            ));
        }
    }

    @Mutation
    public RESET_UNIT_GROUPINGS() {
        this.unitGroupings = [];
    }

    @Mutation
    public RESET_ALL_UNIT_GROUPINGS() {
        this.allGroupings = [];
    }

    @Mutation
    public REMOVE_UNIT_FROM_GROUPING_LIST(unitId: number) {
        this.activeGroupingUnits = this.activeGroupingUnits.filter((unit: GroupingUnitItem) => unit.id !== unitId);
    }
}

